import { Icon } from '@iconify/react/dist/iconify.js'
import { Autocomplete, Box, useTheme } from '@mui/material'
import { useCallback, useState } from 'react'
import { Modal } from '@/components/molecules'
import { Button } from '../../button'
import { Chip } from '../../chip/components/Chip'
import { Input } from '../../input'
import { PropsSelect } from '../types/Select.type'
import { getSize, handleAutocompleteStyle } from '../utils'

/**
 * Komponen Select adalah elemen antarmuka pengguna yang serbaguna yang dirancang untuk berbagai tugas
 * entri data. Komponen ini sangat dapat disesuaikan, menampilkan tiga properti utama: States, Ukuran,
 * dan Jenis. Setiap properti ini menawarkan berbagai opsi untuk beradaptasi dengan berbagai kasus
 * penggunaan dan kebutuhan pengguna.
 */
export const Select = <T,>({
  data,
  selected,
  iconKey,
  labelKey,
  valueKey,
  onChange,
  disabled,
  variant = 'default',
  error,
  placeholder,
  label,
  readOnly,
  size = 'medium',
  labelId,
  setSelected,
  limitTags = 2,
  modalConfirmSelected,
  modalDropdown,
  isAddOption,
  isAddOptionText = 'Add New Option',
  ...rest
}: PropsSelect<T>) => {
  const [selectedValue, setSelectedValue] = useState<T[]>(Array.isArray(selected) ? selected : [])
  const [openDialog, setOpenDialog] = useState(false)
  const [tempValue, setTempValue] = useState<T[]>([])
  const theme = useTheme()

  const handleChange = (_: any, value: any) => {
    setSelectedValue(value || [])
    if (setSelected) {
      setSelected(value)
    }
    if (Array.isArray(value)) {
      onChange?.(value)
    } else {
      onChange?.(value as T)
    }
  }

  const handleCancel = useCallback(() => {
    setOpenDialog(false)
    setTempValue(selectedValue)
    if (modalConfirmSelected?.onClose) {
      modalConfirmSelected.onClose()
    }
    if (modalDropdown?.onClose) {
      modalDropdown.onClose()
    }
  }, [selectedValue])

  const handleConfirm = () => {
    setSelectedValue(tempValue)
    setOpenDialog(false)
    onChange?.(tempValue)
    if (setSelected) {
      setSelected(tempValue)
    }

    if (modalConfirmSelected?.onOk) {
      modalConfirmSelected.onOk()
    }

    if (modalDropdown?.onOk) {
      modalDropdown.onOk()
    }
  }

  const onDelete = useCallback(
    (valueToDelete: T) => {
      if (Array.isArray(selectedValue)) {
        const newSelectedValues = selectedValue.filter(
          item => (item as any)?.[valueKey] !== (valueToDelete as any)?.[valueKey]
        )
        setSelectedValue(newSelectedValues)
        onChange?.(newSelectedValues)
        if (setSelected) {
          setSelected(newSelectedValues)
        }
      }
    },
    [selectedValue, onChange, setSelected, valueKey]
  )

  const handleSelectAll = useCallback(() => {
    setSelectedValue(data)
    onChange?.(data)
    if (setSelected) {
      setSelected(data)
    }
  }, [data, onChange, setSelected])

  const handleClearSelection = useCallback(() => {
    setSelectedValue([])
    onChange?.([])
    if (setSelected) {
      setSelected([])
    }
  }, [onChange, setSelected])

  const onAddNewDropwdown = () => {
    setOpenDialog(true)
  }

  const renderOption = (index: number, totalOptions: number) => (props: any, option: T) => {
    const isLastOption = index === totalOptions - 1
    const isMultiple = isLastOption && variant === 'multiple'

    return (
      <Box key={(option as any)?.[valueKey]} display='flex' flexDirection='column' height='100%' zIndex={9999}>
        <Box flex='1' overflow='auto' mb={isMultiple ? 10 : 0}>
          <Box component='li' sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
            {option && (option as any)?.[iconKey ?? ''] && (
              <Box sx={{ mr: 2 }}>
                <Icon icon={(option as Record<string, any>)?.[iconKey ?? ''] as string} fontSize={12} />
              </Box>
            )}
            {option && (option as any)?.[labelKey ?? '']}
          </Box>
          {isLastOption && isAddOption && (
            <Box
              width={'100%'}
              sx={{
                paddingTop: '8px',
                paddingLeft: '8px'
              }}
            >
              <Button
                size='medium'
                variant='text'
                content='iconText'
                icon='tabler:plus'
                text={isAddOptionText}
                onClick={onAddNewDropwdown}
                sx={{ minWidth: 0, paddingX: '12px', paddingY: '8px' }}
              />
            </Box>
          )}
        </Box>
        {isMultiple && (
          <Box
            display='flex'
            padding='5px'
            borderRadius='6px'
            sx={{ background: theme.colorToken.background.neutral.normal }}
            alignItems='center'
            position='fixed'
            bottom={0}
            left={0}
            right={0}
            zIndex={1}
          >
            <Button
              size='small'
              variant='plain'
              content='textOnly'
              text='Select All'
              onClick={handleSelectAll}
              sx={{ mx: 1, width: '50%' }}
            />
            <Button
              size='small'
              variant='plain'
              content='textOnly'
              text='Clear Selection'
              onClick={handleClearSelection}
              sx={{ width: '50%' }}
            />
          </Box>
        )}
      </Box>
    )
  }

  return (
    <>
      <Autocomplete
        size={size}
        onChange={handleChange}
        options={data}
        getOptionLabel={option => (option as any)?.[labelKey ?? ''] || ''}
        value={selected}
        defaultValue={selected}
        isOptionEqualToValue={(option, value) => (option as any)?.[valueKey] === (value as any)?.[valueKey]}
        disabled={disabled}
        readOnly={readOnly}
        multiple={variant === 'multiple'}
        limitTags={limitTags}
        renderInput={params => (
          <Input
            error={!!error}
            helperText={rest?.helperText ?? ''}
            placeholder={placeholder}
            {...params}
            {...(label ? { label } : {})}
          />
        )}
        renderTags={(value: T[], getTagProps) =>
          value.map((option: T, index: number) => {
            const { key, ...tagProps } = getTagProps({ index })
            return (
              <Chip
                disabled={disabled}
                onMouseDown={e => e.stopPropagation()}
                onDelete={() => onDelete(option)}
                key={key}
                sx={{
                  margin: 1,
                  height: getSize.chipSize(size),
                  fontSize: getSize.fontSize(size)
                }}
                label={(option as any)?.[labelKey ?? '']}
                onClick={() => onDelete(option)}
              />
            )
          })
        }
        renderOption={(props, option, state) => {
          const index = state.index
          return renderOption(index, data.length)(props, option)
        }}
        disableClearable={(selected || rest.value) && variant === 'default' ? false : true}
        forcePopupIcon={true}
        popupIcon={<Icon icon='tabler:chevron-down' fontSize={getSize.iconSize()} />}
        sx={[
          {
            width: rest?.fullWidth ? '100%' : undefined,
            '& .MuiInputBase-root': {
              padding: handleAutocompleteStyle(size)?.padding
            }
          }
        ]}
        {...rest}
      />

      {(modalConfirmSelected || modalDropdown) && (
        <Modal
          title={modalConfirmSelected?.title || modalDropdown?.title}
          description={modalConfirmSelected?.description || modalDropdown?.description}
          positiveLabel={modalConfirmSelected?.positiveLabel || modalDropdown?.positiveLabel}
          isOpen={openDialog}
          onClose={handleCancel}
          onOk={handleConfirm}
        >
          {modalConfirmSelected?.children || modalDropdown?.children}
        </Modal>
      )}
    </>
  )
}
